import React, { useState, useEffect } from 'react';
import { CheckSquare, Trophy, Award, Target, TrendingUp } from 'lucide-react';
import './vStyles/BadgePage.css';
import { UserImpactStats, BadgeLevel } from './types';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import { getAuthToken } from '../../services/api';
import axios from 'axios';

// Define BadgeProgress type
type BadgeProgress = {
  current: number;
  badge: BadgeLevel;
  nextLevel: number;
  percentage: number;
};

// Badge thresholds for tasks
const BADGE_THRESHOLDS = {
  Bronze: 10,
  Silver: 25,
  Gold: 50,
  Platinum: 100
};

// Calculate badge progress
const calculateBadgeProgress = (tasksCompleted: number): BadgeProgress => {
  let badge: BadgeLevel = 'Bronze';
  let nextLevel = BADGE_THRESHOLDS.Silver;

  if (tasksCompleted >= BADGE_THRESHOLDS.Platinum) {
    badge = 'Platinum';
    nextLevel = BADGE_THRESHOLDS.Platinum;
  } else if (tasksCompleted >= BADGE_THRESHOLDS.Gold) {
    badge = 'Gold';
    nextLevel = BADGE_THRESHOLDS.Platinum;
  } else if (tasksCompleted >= BADGE_THRESHOLDS.Silver) {
    badge = 'Silver';
    nextLevel = BADGE_THRESHOLDS.Gold;
  } else if (tasksCompleted >= BADGE_THRESHOLDS.Bronze) {
    badge = 'Bronze';
    nextLevel = BADGE_THRESHOLDS.Silver;
  } else {
    nextLevel = BADGE_THRESHOLDS.Bronze;
  }

  const percentage = Math.min((tasksCompleted / nextLevel) * 100, 100);

  return {
    current: tasksCompleted,
    badge,
    nextLevel,
    percentage
  };
};

// Get badge description
const getBadgeDescription = (badge: BadgeLevel): string => {
  const descriptions = {
    Bronze: 'Complete 10 tasks to earn Bronze badge',
    Silver: 'Complete 25 tasks to earn Silver badge',
    Gold: 'Complete 50 tasks to earn Gold badge',
    Platinum: 'Complete 100 tasks to earn Platinum badge'
  };
  return descriptions[badge];
};

const BadgePage: React.FC = () => {
  const [userStats, setUserStats] = useState<UserImpactStats | null>(null);
  const [badgeProgress, setBadgeProgress] = useState<BadgeProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const { volunteerId } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

  useEffect(() => {
    const fetchUserData = async () => {
      if (!volunteerId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch badge stats from API
        const token = getAuthToken();
        const statsHeaders: Record<string, string> = {
          'user-id': volunteerId.toString(),
          'Content-Type': 'application/json'
        };
        if (token) {
          statsHeaders['Authorization'] = `Bearer ${token}`;
        }
        
        const statsResponse = await axios.get(`${API_BASE_URL}/api/volunteer/${volunteerId}/task-stats`, {
          headers: statsHeaders
        });
        
        const statsData = statsResponse.data as {
          completed_tasks: number;
          current_badge: string;
          next_badge: string;
          progress_percentage: number;
          tasks_remaining: number;
        };
        
        // Fetch project contributions
        const projectsHeaders: Record<string, string> = {};
        if (token) {
          projectsHeaders['Authorization'] = `Bearer ${token}`;
        }
        
        const projectsResponse = await axios.get(`${API_BASE_URL}/api/volunteer/${volunteerId}/joined-projects`, {
          headers: projectsHeaders
        });
        const projectsCount = (projectsResponse.data as any[]).length;
        
        // Calculate impact score (tasks completed * 10 + projects * 5)
        const impactScore = statsData.completed_tasks * 10 + projectsCount * 5;
        
        const userData: UserImpactStats = {
          totalTasksCompleted: statsData.completed_tasks,
          totalHoursContributed: 0,
          currentStreak: 0,
          longestStreak: 0,
          projectsContributed: projectsCount,
          impactScore: impactScore
        };
        
        setUserStats(userData);
        setBadgeProgress(calculateBadgeProgress(userData.totalTasksCompleted));
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [volunteerId, API_BASE_URL]);

  const getBadgeColor = (badge: BadgeLevel): string => {
    const colors = {
      Bronze: '#ea580c',
      Silver: '#6b7280',
      Gold: '#eab308',
      Platinum: '#7c3aed'
    };
    return colors[badge];
  };

  const getBadgeIcon = (badge: BadgeLevel) => {
    return <Trophy size={48} color={getBadgeColor(badge)} />;
  };

  if (loading) {
    return (
      <div className="badge-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your achievements...</p>
        </div>
      </div>
    );
  }

  if (!userStats || !badgeProgress) {
    return (
      <div className="badge-page">
        <div className="error-container">
          <p>Unable to load badge data. Please try again later.</p>
        </div>
      </div>
    );
  }

  // Calculate tasks until next badge
  const tasksUntilNext = badgeProgress.nextLevel - badgeProgress.current;
  const isMaxLevel = badgeProgress.badge === 'Platinum' && badgeProgress.current >= BADGE_THRESHOLDS.Platinum;

  return (
    <div className="badge-page">
      {/* Header */}
      <div className="badge-header">
        <div className="header-title">
          <Award className="header-icon" />
          <h1>My Achievements</h1>
        </div>
        <p className="header-subtitle">Track your task completion progress and earn badges!</p>
      </div>

      {/* Main Badge Display */}
      <div className="main-badge-section">
        <div className="current-badge-display">
          <div className="badge-icon-large">
            {getBadgeIcon(badgeProgress.badge)}
          </div>
          <div className="badge-details">
            <h2 className="current-badge-title">{badgeProgress.badge} Badge</h2>
            <p className="badge-description">
              {isMaxLevel 
                ? 'Congratulations! You\'ve reached the highest level!' 
                : `${tasksUntilNext} more tasks to reach ${
                    badgeProgress.badge === 'Bronze' ? 'Silver' :
                    badgeProgress.badge === 'Silver' ? 'Gold' :
                    badgeProgress.badge === 'Gold' ? 'Platinum' : 'maximum'
                  } badge`
              }
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {!isMaxLevel && (
          <div className="progress-container">
            <div className="progress-info">
              <span className="progress-current">{badgeProgress.current} tasks completed</span>
              <span className="progress-target">Goal: {badgeProgress.nextLevel} tasks</span>
            </div>
            <div className="progress-bar-wrapper">
              <div 
                className="progress-bar-fill"
                style={{ 
                  width: `${badgeProgress.percentage}%`,
                  backgroundColor: getBadgeColor(badgeProgress.badge)
                }}
              >
                <span className="progress-percentage">{Math.round(badgeProgress.percentage)}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Badge Milestones */}
      <div className="milestones-section">
        <h2 className="section-title">Badge Milestones</h2>
        <div className="milestones-grid">
          {Object.entries(BADGE_THRESHOLDS).map(([level, threshold]) => {
            const achieved = userStats.totalTasksCompleted >= threshold;
            const badgeLevel = level as BadgeLevel;
            
            return (
              <div 
                key={level} 
                className={`milestone-card ${achieved ? 'achieved' : 'locked'}`}
              >
                <div className="milestone-icon">
                  {achieved ? (
                    <Trophy size={32} color={getBadgeColor(badgeLevel)} />
                  ) : (
                    <Trophy size={32} color="#d1d5db" />
                  )}
                </div>
                <h3 className="milestone-title">{level}</h3>
                <p className="milestone-threshold">{threshold} Tasks</p>
                {achieved ? (
                  <span className="milestone-status achieved-status">✓ Achieved</span>
                ) : (
                  <span className="milestone-status locked-status">
                    {threshold - userStats.totalTasksCompleted} to go
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Statistics Summary */}
      <div className="stats-summary">
        <h2 className="section-title">Your Impact</h2>
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon completed">
              <CheckSquare size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{userStats.totalTasksCompleted}</span>
              <span className="stat-label">Tasks Completed</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon projects">
              <Target size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{userStats.projectsContributed}</span>
              <span className="stat-label">Projects Contributed</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon impact">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{userStats.impactScore}</span>
              <span className="stat-label">Impact Score</span>
            </div>
          </div>
        </div>
      </div>

      {/* Motivational Section */}
      <div className="motivation-section">
        <div className="motivation-card">
          <h3>Keep Going! 🎯</h3>
          <p>
            {isMaxLevel 
              ? 'Amazing work! You\'ve achieved the highest badge level. Keep making an impact!'
              : `You're doing great! Complete ${tasksUntilNext} more ${tasksUntilNext === 1 ? 'task' : 'tasks'} to unlock your next achievement!`
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default BadgePage;